import '@splidejs/splide/dist/css/splide.min.css';

import React, { useEffect, useMemo } from 'react';
import { flatten, size } from 'lodash-es';
import { queryClient, useAllTestResultPositions } from '@js/api';

import { QueryClientProvider } from 'react-query';

import SeasonSwitch from '@components/TrialMaps/SeasonSwitch';
import MapDisplay, { defaultProps } from '@components/TrialMaps/Map';
import MapOverlay from '@components/TrialMaps/MapOverlay';
import MapNav from '@components/TrialMaps/MapNav';

function Map({
  center,
  zoom,
  category,
  product,
  mainColor,
  seasons
} = defaultProps) {
  const query = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);

  const [type, setType] = React.useState('testimony'); // "lg" | "independent" | "testimony"
  const [selectedTestResult, setSelectedTestResult] = React.useState(null);
  const [season, setSeason] = React.useState('');

  const { data, isSuccess } = useAllTestResultPositions({
    category,
    variety: product,
    season,
    featureAvIds: '19',
    testimony_types: 'hdi_review,silo_comparison',
    independentType: 'hdi'
  });

  useEffect(() => {
    if (query && query?.get('type')) {
      setType(query.get('type'));
    }
  }, [query]);

  useEffect(() => {
    if (!data) return;

    const flatArr = flatten(Object.values(data));
    if (
      !flatArr.some((r) => r.type.startsWith('testimony')) &&
      flatArr.some((r) => r.type === 'intern')
    ) {
      setType('lg');
    } else if (
      !flatArr.some((r) => r.type.startsWith('intern')) &&
      flatArr.some((r) => r.type === 'independent')
    ) {
      setType('independent');
    }
  }, [data]);

  const results = useMemo(() => {
    if (type === 'lg') {
      return data?.['intern'];
    }
    if (type === 'independent') {
      return data?.['independent'];
    }
    if (type === 'testimony') {
      return data?.['testimony'];
    }

    return [];
  }, [type, data]);

  if (!results) return null;

  return (
    <div className="pb-24">
      <div className="flex items-center justify-between">
        <MapNav
          results={results}
          type={type}
          handleClick={setType}
          hasIndependent={size(data?.['independent'])}
          hasIntern={size(data?.['intern'])}
          hasTestimony={size(data?.['testimony'])}
          category={category}
        />
        {Array.isArray(seasons) && seasons.length > 1 ? (
          <SeasonSwitch
            seasons={seasons}
            selected={season}
            handleChange={(opt) => {
              setSeason(opt);
            }}
          />
        ) : null}
      </div>
      <MapDisplay
        styleType="hdi"
        results={results}
        season={season}
        isSuccess={isSuccess}
        type={type}
        center={center}
        zoom={zoom}
        handleClick={(id, type) => {
          setSelectedTestResult({ id, type });
        }}
      />
      {selectedTestResult ? (
        <>
          <div className="MapOverlay-background"></div>
          <MapOverlay
            id={selectedTestResult.id}
            type={selectedTestResult.type}
            category={category}
            product={product}
            handleClose={() => setSelectedTestResult(false)}
            mainColor={mainColor}
            featureAvIds="19"
          />
        </>
      ) : null}
    </div>
  );
}

function MapWrapper(props) {
  return (
    <QueryClientProvider client={queryClient}>
      <Map {...props} />
    </QueryClientProvider>
  );
}

export default MapWrapper;
